"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { FiCheck, FiSearch } from "react-icons/fi";
import { LuLoader2 } from "react-icons/lu";
import { Button, Input } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useUser } from "@/contexts/user-provider";
import { useDebounce } from "@/hooks/use-debounce";
import { formatDate } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { getNotes } from "@/api/provider/scribe/notes";
interface NotePickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  currentNoteId?: string | null;
  linkedNoteIds?: string[];
  onSave: (noteIds: string[]) => void;
  isSaving?: boolean;
}
export default function NotePickerModal({
  open,
  onOpenChange,
  patientId,
  currentNoteId,
  linkedNoteIds = [],
  onSave,
  isSaving = false
}: NotePickerModalProps) {
  const {
    currentUser
  } = useUser();
  const canViewNotes = hasPermission(currentUser, PERMISSIONS.CLINICAL_NOTES_READ);
  const [selectedNoteIds, setSelectedNoteIds] = useState<Set<string>>(() => new Set(linkedNoteIds));
  const initialSet = useMemo(() => new Set(linkedNoteIds),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [linkedNoteIds.join(",")]);
  const hasChanged = useMemo(() => {
    if (selectedNoteIds.size !== initialSet.size) return true;
    return Array.from(selectedNoteIds).some(id => !initialSet.has(id));
  }, [selectedNoteIds, initialSet]);
  const [search, setSearch] = useState("");

  // Reset selection when modal opens or linkedNoteIds change
  const linkedKey = linkedNoteIds.join(",");
  const [prevState, setPrevState] = useState({
    open,
    linkedKey
  });
  if (open !== prevState.open || linkedKey !== prevState.linkedKey) {
    setPrevState({
      open,
      linkedKey
    });
    if (open) {
      setSelectedNoteIds(new Set(linkedNoteIds));
      setSearch("");
    }
  }
  const debouncedSearch = useDebounce(search, 300);
  const {
    data: notesPage,
    isLoading
  } = useQuery({
    queryKey: ["notes", "list", patientId, debouncedSearch],
    queryFn: () => getNotes({
      patientId,
      page: 1,
      perPage: 50,
      ...(debouncedSearch ? {
        patientName: debouncedSearch
      } : {})
    }),
    enabled: open && !!patientId && canViewNotes
  });
  const notes = notesPage?.data ?? [];
  const toggleNote = (noteId: string) => {
    setSelectedNoteIds(prev => {
      const next = new Set(prev);
      if (next.has(noteId)) {
        next.delete(noteId);
      } else {
        next.add(noteId);
      }
      return next;
    });
  };
  const handleConfirm = () => {
    onSave(Array.from(selectedNoteIds));
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Link to Note</DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-2">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search notes..." className="pl-9" autoFocus />
          </div>
        </div>
        <DialogBody className="max-h-[400px] overflow-y-auto p-0">
          {!canViewNotes ? <div className="p-8 text-center text-body text-foreground-secondary">
              You don&apos;t have permission to view notes
            </div> : isLoading ? <div className="flex items-center justify-center p-8">
              <LuLoader2 className="h-5 w-5 animate-spin text-foreground-muted" />
            </div> : notes.length === 0 ? <div className="p-8 text-center text-body text-foreground-secondary">
              {search ? "No notes match your search." : "No notes found for this patient."}
            </div> : <div className="divide-y">
              {notes.map(note => {
            const isCurrentNote = note.id === currentNoteId;
            const isSelected = selectedNoteIds.has(note.id);
            const isAlreadyLinked = initialSet.has(note.id);
            return <button key={note.id} onClick={() => toggleNote(note.id)} className={cx("flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-hover", isSelected && "bg-primary/10")}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-body-strong text-foreground truncate">
                          {note.summary || "Untitled Note"}
                        </span>
                        {isCurrentNote && <span className="shrink-0 rounded bg-status-info-bg px-1.5 py-0.5 text-tiny font-medium text-status-info-text">
                            Current
                          </span>}
                        {isAlreadyLinked && <span className="shrink-0 rounded bg-status-success-bg px-1.5 py-0.5 text-tiny font-medium text-status-success-text">
                            Linked
                          </span>}
                      </div>
                      <div className="text-caption text-foreground-secondary">
                        {formatDate(note.createdDate)}
                        {note.providers?.[0] && <span>{` \u00B7 ${note.providers[0].firstName} ${note.providers[0].lastName}`}</span>}
                      </div>
                    </div>
                    {isSelected && <FiCheck className="h-4 w-4 shrink-0 text-primary" />}
                  </button>;
          })}
            </div>}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!hasChanged || isSaving} isLoading={isSaving}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}